import { React, Fragment, useEffect, useState, useTheme, useRef } from '../lib/imports';
import LeaderboardTable from '../leaderboard/LeaderboardTable';
import LeaderboardNav from '../leaderboard/LeaderboardNav';
import LeaderboardToggle from '../leaderboard/LeaderboardToggle';
import LeaderboardBanner from '../leaderboard/LeaderboardBanner';
import PersonalRanking from '../leaderboard/PersonalRanking';
import { fetchLeaderboardData, fetchLocalLeaderboardData, setBoardView } from '../leaderboard/actions';
import Button from '../components/chrome/Button';
import styled from '@emotion/styled';
import { useCdnUrl } from '../app/context/config';
import { batch, useDispatch, useSelector } from 'react-redux';
import { fireMessageEvent } from '../messenger/utils';
import { analyticActionMap } from '../analytics/utils';
import { SEASON_CODE } from '../lib/constants';
import ComingSoon from '../leaderboard/ComingSoon';

const StyledLeaderboardFooter = styled.div`
  position: sticky;
  bottom: 0px;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ErrorState = styled.div`
  background-color: ${({ theme }) => theme.colors.r500};
  color: ${({ theme }) => theme.colors.white};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  text-align: center;
  padding: ${({ theme }) => theme.spacings.giga};
  ${({ theme }) => theme.typography.text.giga};
`;

const Leaderboard = () => {
  const cdnUrl = useCdnUrl();
  const theme = useTheme();

  const listItemRef = useRef();
  const boardToggleRef = useRef();

  const dispatch = useDispatch();
  const { strings, leaderboard, config } = useSelector((state) => state);
  const { platform, env, language } = config;

  const {
    currentChallenge,
    isCurrentlyPlayoffs,
    isCurrentlySeason,
    localLeaderboardData,
    leaderboardData,
    selectedBoard,
    error,
    userStats,
    selectedChallenge,
  } = leaderboard;
  const { clickedType, path, buttonType, seeYourPosition, scrollToTop } = analyticActionMap;
  const { username, scoringStreak } = userStats;
  const challengeNumber = selectedChallenge || currentChallenge;
  const [viewedChallenge, setViewedChallenge] = useState(challengeNumber);
  const currentView = selectedBoard;
  const contestantData = leaderboardData.users || [];
  const localContestantData = localLeaderboardData.users || [];

  const currentChallengeUserRank =
    {
      season: userStats.season?.rank,
      monthly: userStats.challenges?.[challengeNumber]?.rank,
      playoffs: userStats.postseason?.rank,
    }[selectedBoard] ?? null;

  const isTopRanked =
    contestantData &&
    contestantData.some((contestant) => {
      return contestant.me === true;
    });
  let displayedLocalData;
  let displayedRankData;

  const fetchError = error;

  const leaderboardBtnText = strings['button/leaderboard/showPosition'];
  const scrollToTopText = strings['button/leaderboard/scrollToTop'];
  const errorStateText = strings['leaderboard/error/error-state'];
  const seasonText = strings['leaderboard/toggle/season'];
  const monthlyText = strings['leaderboard/toggle/monthly'];
  const playoffsText = strings['leaderboard/toggle/playoffs'];
  const seasonBannerText = {
    header: strings['leaderboard/banner/season/header'],
    date: strings['leaderboard/banner/season/date'],
    paragraph: strings['leaderboard/banner/season/paragraph'],
    link: strings['leaderboard/banner/season/link'],
  };
  const monthlyBannerText = {
    header: strings['leaderboard/banner/monthly/header'],
    date: strings['leaderboard/banner/monthly/date'][challengeNumber - 1],
    paragraph: strings['leaderboard/banner/monthly/paragraph'],
    link: strings['leaderboard/banner/season/link'],
  };
  const playoffsBannerText = {
    header: strings['leaderboard/banner/playoffs/header'],
    date: strings['leaderboard/banner/playoffs/date'],
    paragraph: strings['leaderboard/banner/playoffs/paragraph'],
    link: strings['leaderboard/banner/playoffs/link'],
    route: strings['leaderboard/banner/playoffs/route'],
  };

  const inProgressText = strings['leaderboard/inProgress/text'];
  const completedText = strings['leaderboard/completed/text'];

  const imageOptions = {
    season: [
      {
        src: `${cdnUrl}2025-2026_Season_Prize.png`,
        width: '100%',
        rightMargin: theme.spacings.kilo,
      },
    ],
    monthly: [
      {
        src: `${cdnUrl}2026_monthly_prizes_${language}.png`,
        width: '100%',
        rightMargin: theme.spacings.mega,
        leftMargin: theme.spacings.mega,
      },
    ],
    playoffs: [
      {
        src: `${cdnUrl}2025_Playoffs_Prize_${language}.png`,
        width: '100%',
        bottomMargin: theme.spacings.kilo,
      },
    ],
  };

  const [btnText, setBtnText] = useState(leaderboardBtnText);
  const [leaderBoardPos, setLeaderBoardPos] = useState('standard');

  const options = [
    { key: 'season', value: seasonText },
    { key: 'monthly', value: monthlyText },
    { key: 'playoffs', value: playoffsText },
  ];

  const displayedLeaderboardData = (chosenBoard) => {
    switch (chosenBoard) {
      case 'season':
        if (!isTopRanked && currentChallengeUserRank) {
          displayedLocalData = localContestantData || [];
        } else {
          displayedLocalData = [];
        }
        displayedRankData = [contestantData, displayedLocalData];

        return {
          ...userStats.season,
          contestants: displayedRankData,
          isTopRanked,
        };
      case 'monthly':
        if (!isTopRanked && currentChallengeUserRank) {
          displayedLocalData = localContestantData || [];
        } else {
          displayedLocalData = [];
        }
        displayedRankData = [contestantData, displayedLocalData];

        return {
          ...userStats.challenges[challengeNumber],
          contestants: displayedRankData,
          isTopRanked,
        };
      case 'playoffs':
        if (!isTopRanked && currentChallengeUserRank) {
          displayedLocalData = localContestantData || [];
        } else {
          displayedLocalData = [];
        }
        displayedRankData = [contestantData, displayedLocalData];
        return {
          ...userStats.postseason,
          contestants: displayedRankData,
          isTopRanked,
        };
    }
  };

  const personalRankData = (chosenBoard) => {
    const shownChallengeIndex = selectedChallenge || currentChallenge;
    switch (chosenBoard) {
      case 'season':
        return {
          ...userStats.season,
          selectedBoard,
          currentChallenge: null,
          scoringStreak,
          selectedChallenge,
          username,
        };
      case 'monthly':
        return {
          ...userStats.challenges[shownChallengeIndex],
          selectedBoard,
          currentChallenge,
          scoringStreak,
          selectedChallenge,
          username,
        };

      case 'playoffs':
        return {
          ...userStats.postseason,
          contestants: displayedRankData,
          isTopRanked,
        };
    }
  };

  const bannerTextOptions = {
    season: seasonBannerText,
    monthly: monthlyBannerText,
    playoffs: playoffsBannerText,
  };

  useEffect(() => {
    boardToggleRef.current.scrollIntoView({ block: 'center' });
  }, [challengeNumber]);

  const batchFetchLeaderboardData = (board, challengeNumber) => {
    // Season URI param needs to be passed to retrieve past monthly challenges when playoffs season is active
    const season = board === 'monthly' && isCurrentlyPlayoffs && !isCurrentlySeason ? SEASON_CODE : undefined;
    batch(() => {
      dispatch(fetchLeaderboardData(board, challengeNumber, season));
      dispatch(fetchLocalLeaderboardData(board, true, challengeNumber, season));
    });
  };

  useEffect(() => {
    if ((selectedBoard === 'season' || selectedBoard === 'monthly') && leaderboardData.status === 'closed') {
      dispatch(setBoardView('playoffs'));
      batchFetchLeaderboardData('playoffs');
    }
  }, []);

  const handleViewLeaderboardTab = (viewId) => {
    dispatch(setBoardView(viewId));

    switch (viewId) {
      case 'monthly':
        batchFetchLeaderboardData(viewId, viewedChallenge);
        break;
      case 'playoffs':
        batchFetchLeaderboardData(viewId);
        break;
      default:
        batchFetchLeaderboardData(viewId);
        break;
    }
  };

  const handleChallengeChange = (selection) => {
    setViewedChallenge(selection);
    batchFetchLeaderboardData(currentView, selection);
  };

  const scrollToPosition = () => {
    const analyticsTitle = btnText === scrollToTopText ? scrollToTop : seeYourPosition;
    fireMessageEvent({
      platform,
      env,
      title: clickedType,
      data: {
        path: path,
        element: buttonType,
        element_name: analyticsTitle,
      },
    });
    if (leaderBoardPos === 'standard') {
      setLeaderBoardPos('local');
      setBtnText(scrollToTopText);
      listItemRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      setLeaderBoardPos('standard');
      setBtnText(leaderboardBtnText);
      boardToggleRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const LeaderBoardContent = (chosenboard) => {
    return (
      <>
        <LeaderboardBanner
          text={bannerTextOptions[currentView]}
          images={imageOptions[currentView]}
          inProgressText={inProgressText}
          completedText={completedText}
          showAsCompleted={leaderboardData.status !== 'current'}
          language={language}
        />
        {chosenboard === 'monthly' && (
          <LeaderboardNav currentChallenge={currentChallenge} totalChallenges={6} onChange={handleChallengeChange} />
        )}
        {fetchError && <ErrorState>{errorStateText}</ErrorState>}
        <LeaderboardTable
          data={displayedLeaderboardData(selectedBoard)}
          selectedView={currentView}
          options={options}
          ref={listItemRef}
        />
        <StyledLeaderboardFooter>
          {currentChallengeUserRank && (
            <>
              <Button variant='primary' onSelect={scrollToPosition}>
                {btnText}
              </Button>
              <PersonalRanking data={personalRankData(selectedBoard)} selectedBoard={selectedBoard} />
            </>
          )}
        </StyledLeaderboardFooter>
      </>
    );
  };

  const seasonOrMonthly = () => {
    if (selectedBoard === 'season') {
      return LeaderBoardContent('season');
    } else if (selectedBoard === 'monthly') {
      return LeaderBoardContent('monthly');
    }
  };
  const playoffPage = () => {
    if (!isCurrentlyPlayoffs && !isCurrentlySeason) {
      return LeaderBoardContent();
    }
    return !isCurrentlyPlayoffs ? <ComingSoon /> : LeaderBoardContent();
  };
  return (
    <>
      <div ref={boardToggleRef}>
        <LeaderboardToggle options={options} handleViewToggle={handleViewLeaderboardTab} currentView={currentView} />
      </div>
      {selectedBoard !== 'playoffs' ? seasonOrMonthly() : playoffPage()}
    </>
  );
};

export default Leaderboard;
